import './SearchPlayers.css';
import React, { useState, useEffect, useRef } from 'react';
import latinize from 'latinize';
import debounce from 'lodash.debounce';
import { getImgSrc } from '../../helpers/getImageSrc';

const backendURL = (process.env.REACT_APP_BACKEND_URL || 'https://api.footy-games.com.ar');

// `placeholder` es opcional: por defecto mantiene el texto que ya usaban el resto de
// los juegos, y permite que Build11 muestre el suyo en español.
// `lockedHint` es opcional: Modo 412 lo usa para deshabilitar el buscador hasta que se
// elija una casilla (y mostrar un placeholder propio en vez de "Wait for your turn…").
const SearchPlayers = ({ onSelect, turn, player, league, placeholder = 'Search player…', lockedHint = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [focused, setFocused] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef     = useRef(null);
  const dropRef      = useRef(null);
  const abortRef     = useRef(null);
  const debounceRef  = useRef(null);

  // Recrear la función debounced cuando cambia el league
  useEffect(() => {
    debounceRef.current = debounce(async (value) => {
      if (value.length < 2) { setResults([]); setOpen(false); return; }

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      try {
        const norm = latinize(value.toLowerCase());
        const url = league === 'arg'
          ? `${backendURL}/players/arg/search?search=${norm}`
          : `${backendURL}/players/search?search=${norm}`;
        const res = await fetch(url, { signal: abortRef.current.signal });
        if (!res.ok) return;
        const data = await res.json();
        setResults(data);
        setOpen(data.length > 0);
        setFocused(0);
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    }, 350);

    return () => debounceRef.current?.cancel();
  }, [league]);

  // Disparar búsqueda cuando cambia el query
  useEffect(() => {
    debounceRef.current?.(query);
    return () => {
      debounceRef.current?.cancel();
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (p) => {
    onSelect(p);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(f => Math.max(f - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (results[focused]) handleSelect(results[focused]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  const disabled = turn !== player || lockedHint === 'wait-square';

  const birthYear = (p) => {
    const parts = (p.fecha_nacimiento || '').split('.');
    return parts[parts.length - 1] || '';
  };

  return (
    <div className="sp-wrap" ref={dropRef}>
      <div className={`sp-input-box${disabled ? ' sp-disabled' : ''}`}>
        <svg className="sp-search-icon" viewBox="0 0 20 20" fill="none">
          <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        <input
          ref={inputRef}
          className="sp-input"
          type="text"
          placeholder={
            lockedHint === 'wait-square'
              ? 'Elegí una casilla primero…'
              : (disabled ? 'Wait for your turn…' : placeholder)
          }
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          disabled={disabled}
          autoComplete="off"
        />
        {query && !disabled && (
          <button className="sp-clear" onClick={() => { setQuery(''); setResults([]); setOpen(false); inputRef.current?.focus(); }}>
            ✕
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="sp-dropdown">
          {results.map((p, i) => (
            <div
              key={p._id}
              className={`sp-option${i === focused ? ' sp-option--active' : ''}`}
              onMouseDown={() => handleSelect(p)}
              onMouseEnter={() => setFocused(i)}
            >
              {p.img ? (
                <img className="sp-photo" src={getImgSrc(p.img)} alt="" />
              ) : (
                <div className="sp-photo sp-photo--placeholder">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
                </div>
              )}
              <div className="sp-info">
                <span className="sp-name">{p.nombre}</span>
                <span className="sp-meta">
                  {p.img_pais && <img className="sp-flag" src={p.img_pais} alt="" />}
                  {p.posicion} · {birthYear(p)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchPlayers;
